import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import {
  confirmIndustrialCostTechnicalConsumptionRulesImport,
  createIndustrialCostTechnicalConsumptionRule,
  deleteIndustrialCostTechnicalConsumptionRule,
  discardIndustrialCostTechnicalConsumptionImportPending,
  getIndustrialCostDirectCostCategories,
  getIndustrialCostDirectMaterials,
  getIndustrialCostProducts,
  getIndustrialCostTechnicalConsumptionImportPending,
  getIndustrialCostTechnicalConsumptionRules,
  previewIndustrialCostTechnicalConsumptionRulesImport,
  resolveIndustrialCostTechnicalConsumptionImportPending,
  updateIndustrialCostTechnicalConsumptionRule,
} from '../services/industrialCostService';
import {
  analyzeFileNormalization,
  commitFileNormalization,
  reviewFileNormalization,
} from '../fileNormalization/service';
import type { FileNormalizationAnalyzeResult } from '../fileNormalization/types';
import type {
  CreateDirectMaterialPayload,
  CreateTechnicalConsumptionRulePayload,
  UpdateTechnicalConsumptionRulePayload,
  DirectCostCategory,
  DirectMaterial,
  DirectMaterialType,
  Product,
  TechnicalConsumptionBasis,
  TechnicalConsumptionImportPending,
  TechnicalConsumptionImportPreviewResponse,
  TechnicalConsumptionRule,
} from '../types/industrialCost';
import { IMPORT_PERMISSION_DENIED_TEXT, useCanManageImports } from '../utils/importPermission';

type ResolvePendingMaterialMode = 'existing' | 'create';

type ResolvePendingFormState = {
  product_id: string;
  material_mode: ResolvePendingMaterialMode;
  material_id: string;
  material_code: string;
  material_name: string;
  material_type: DirectMaterialType | '';
  unit_label: string;
  is_active: boolean;
  description: string;
};

type RuleFormState = {
  product_id: string;
  material_id: string;
  quantity: string;
  consumption_basis: '' | TechnicalConsumptionBasis;
  unit_label: string;
  input_type: string;
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  notes: string;
};

const initialForm: RuleFormState = {
  product_id: '',
  material_id: '',
  quantity: '',
  consumption_basis: '',
  unit_label: '',
  input_type: '',
  valid_from: '',
  valid_to: '',
  is_active: true,
  notes: '',
};

const initialResolvePendingForm: ResolvePendingFormState = {
  product_id: '',
  material_mode: 'existing',
  material_id: '',
  material_code: '',
  material_name: '',
  material_type: '',
  unit_label: '',
  is_active: true,
  description: '',
};

const technicalConsumptionImportFields = [
  { key: 'product', label: 'Producto', required: true },
  { key: 'production_line', label: 'Línea de producción', required: false },
  { key: 'material', label: 'Material / input', required: true },
  { key: 'quantity', label: 'Cantidad técnica', required: true },
  { key: 'consumption_basis', label: 'Base de consumo', required: false },
  { key: 'unit_label', label: 'Unidad', required: false },
  { key: 'input_type', label: 'Tipo de input', required: false },
  { key: 'valid_from', label: 'Válida desde', required: false },
  { key: 'valid_to', label: 'Válida hasta', required: false },
  { key: 'is_active', label: 'Activo', required: false },
  { key: 'notes', label: 'Notas', required: false },
] as const;

function formatBasis(value: TechnicalConsumptionBasis | null) {
  if (value === 'per_unit') return 'Por unidad';
  if (value === 'per_kg') return 'Por kg';
  return 'Por unidad (default)';
}

function getCategoryLabel(categories: DirectCostCategory[], type: DirectMaterialType | null | undefined) {
  if (!type) return 'Sin clasificación';
  const category = categories.find((item) => item.code === type);
  return category?.label ?? type;
}

function buildCreateMaterialPayload(form: ResolvePendingFormState): CreateDirectMaterialPayload {
  const code = form.material_code.trim();

  return {
    code,
    name: form.material_name.trim() || code,
    material_type: form.material_type || null,
    unit_label: form.unit_label.trim() || null,
    is_active: form.is_active,
    description: form.description.trim() || null,
  };
}

function formatPendingReason(reason: TechnicalConsumptionImportPending['pending_reason']) {
  if (reason === 'unresolved_product') return 'Producto no resuelto';
  if (reason === 'unresolved_material') return 'Material no resuelto';
  if (reason === 'invalid_quantity') return 'Cantidad inválida';
  if (reason === 'duplicate_in_file') return 'Duplicado en archivo';
  if (reason === 'persistence_error') return 'Error de persistencia';
  return reason;
}

export default function IndustrialCostTechnicalConsumptionsView() {
  const queryClient = useQueryClient();

  const [productFilter, setProductFilter] = useState<'all' | string>('all');
  const [materialFilter, setMaterialFilter] = useState<'all' | string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<TechnicalConsumptionRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<TechnicalConsumptionRule | null>(null);
  const [pendingToDiscard, setPendingToDiscard] = useState<TechnicalConsumptionImportPending | null>(null);
  const [pendingToResolve, setPendingToResolve] = useState<TechnicalConsumptionImportPending | null>(null);
  const [resolvePendingForm, setResolvePendingForm] = useState<ResolvePendingFormState>(initialResolvePendingForm);
  const [form, setForm] = useState<RuleFormState>(initialForm);

  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState<1 | 2 | 3>(1);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<TechnicalConsumptionImportPreviewResponse | null>(null);
  const [importMapping, setImportMapping] = useState<Record<string, string | null>>({});
  const [productResolution, setProductResolution] = useState<Record<string, string | null>>({});
  const [materialResolution, setMaterialResolution] = useState<Record<string, string | null>>({});
  const [materialTypeMapping, setMaterialTypeMapping] =
    useState<Record<string, string | null>>({});
  const [previewingImport, setPreviewingImport] = useState(false);
  const [importMappingDirty, setImportMappingDirty] = useState(false);
  const [quantityDivisor, setQuantityDivisor] = useState('1');
  // IC-STOCK-06: el Excel es el maestro del escandallo.
  const [replaceExistingRules, setReplaceExistingRules] = useState(true);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [normalizationResult, setNormalizationResult] =
    useState<FileNormalizationAnalyzeResult | null>(null);
  const [normalizationReviewOpen, setNormalizationReviewOpen] = useState(false);
  const [normalizationReviewSheetName, setNormalizationReviewSheetName] =
    useState('');
  const [
    normalizationReviewHeaderRowInput,
    setNormalizationReviewHeaderRowInput,
  ] = useState('1');
  const [reviewingNormalization, setReviewingNormalization] = useState(false);
  const [importResultMessage, setImportResultMessage] = useState<string | null>(null);

  const productsQuery = useQuery<Product[]>({
    queryKey: ['industrial-cost-products'],
    queryFn: getIndustrialCostProducts,
  });

  const directMaterialsQuery = useQuery<DirectMaterial[]>({
    queryKey: ['industrial-cost-direct-materials'],
    queryFn: getIndustrialCostDirectMaterials,
  });

  const directCostCategoriesQuery = useQuery<DirectCostCategory[]>({
    queryKey: ['industrial-cost-direct-cost-categories'],
    queryFn: getIndustrialCostDirectCostCategories,
  });

  const rulesQuery = useQuery<TechnicalConsumptionRule[]>({
    queryKey: ['industrial-cost-technical-consumption-rules'],
    queryFn: getIndustrialCostTechnicalConsumptionRules,
  });

  const pendingQuery = useQuery<TechnicalConsumptionImportPending[]>({
    queryKey: ['industrial-cost-technical-consumption-import-pending'],
    queryFn: getIndustrialCostTechnicalConsumptionImportPending,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateTechnicalConsumptionRulePayload) =>
      createIndustrialCostTechnicalConsumptionRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-technical-consumption-rules'],
      });
      setCreateOpen(false);
      setEditingRule(null);
      setForm(initialForm);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      ruleId,
      payload,
    }: {
      ruleId: string;
      payload: UpdateTechnicalConsumptionRulePayload;
    }) => updateIndustrialCostTechnicalConsumptionRule(ruleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-technical-consumption-rules'],
      });
      setCreateOpen(false);
      setEditingRule(null);
      setForm(initialForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => deleteIndustrialCostTechnicalConsumptionRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-technical-consumption-rules'],
      });
      setRuleToDelete(null);
    },
  });

  const discardPendingMutation = useMutation({
    mutationFn: discardIndustrialCostTechnicalConsumptionImportPending,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-technical-consumption-import-pending'],
      });
      setPendingToDiscard(null);
    },
  });

  const resolvePendingMutation = useMutation({
    mutationFn: ({
      pendingId,
      payload,
    }: {
      pendingId: string;
      payload: Parameters<typeof resolveIndustrialCostTechnicalConsumptionImportPending>[1];
    }) => resolveIndustrialCostTechnicalConsumptionImportPending(pendingId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-technical-consumption-import-pending'],
      });
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-technical-consumption-rules'],
      });
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-direct-materials'],
      });
      setPendingToResolve(null);
      setResolvePendingForm(initialResolvePendingForm);
    },
  });

  const rows = rulesQuery.data ?? [];
  const pendingRows = pendingQuery.data ?? [];
  const products = productsQuery.data ?? [];
  const materials = directMaterialsQuery.data ?? [];
  const categories = directCostCategoriesQuery.data ?? [];

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return rows.filter((row) => {
      const searchableValues = [
        row.product_name,
        row.product_code,
        row.material_name,
        row.material_code,
        row.quantity,
        formatBasis(row.consumption_basis),
        row.unit_label,
        row.input_type,
        row.valid_from,
        row.valid_to,
        row.notes,
      ];

      const matchesSearch =
        !normalizedSearch ||
        searchableValues
          .filter((value) => value !== null && value !== undefined)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      if (!matchesSearch) return false;
      if (productFilter !== 'all' && row.product_id !== productFilter) return false;
      if (materialFilter !== 'all' && row.material_id !== materialFilter) return false;
      if (statusFilter === 'active' && !row.is_active) return false;
      if (statusFilter === 'inactive' && row.is_active) return false;
      return true;
    });
  }, [rows, productFilter, materialFilter, searchQuery, statusFilter]);

  const activeRulesCount = rows.filter((row) => row.is_active).length;
  const productsWithRulesCount = new Set(rows.map((row) => row.product_id)).size;
  const materialsUsedCount = new Set(rows.map((row) => row.material_id)).size;
  const productsWithoutRulesCount = Math.max(products.length - productsWithRulesCount, 0);
  const rulesWithoutExplicitBasisCount = rows.filter((row) => !row.consumption_basis).length;

  function openCreateRuleDialog() {
    setEditingRule(null);
    setForm(initialForm);
    setCreateOpen(true);
  }

  function openEditRuleDialog(rule: TechnicalConsumptionRule) {
    setEditingRule(rule);
    setForm({
      product_id: rule.product_id,
      material_id: rule.material_id,
      quantity: String(rule.quantity ?? ''),
      consumption_basis: rule.consumption_basis ?? '',
      unit_label: rule.unit_label ?? '',
      input_type: rule.input_type ?? '',
      valid_from: rule.valid_from ?? '',
      valid_to: rule.valid_to ?? '',
      is_active: rule.is_active,
      notes: rule.notes ?? '',
    });
    setCreateOpen(true);
  }

  function closeRuleDialog() {
    if (createMutation.isPending || updateMutation.isPending) return;

    setCreateOpen(false);
    setEditingRule(null);
    setForm(initialForm);
  }

  const canSubmit =
    form.product_id.trim().length > 0 &&
    form.material_id.trim().length > 0 &&
    form.quantity.trim().length > 0 &&
    !Number.isNaN(Number(form.quantity)) &&
    Number(form.quantity) > 0;

  function handleDeleteRule(rule: TechnicalConsumptionRule) {
    setRuleToDelete(rule);
  }

  function handleDiscardPending(pending: TechnicalConsumptionImportPending) {
    setPendingToDiscard(pending);
  }

  function handleResolvePending(pending: TechnicalConsumptionImportPending) {
    const sourceMaterialRef = pending.source_material_ref?.trim() || pending.normalized_material_ref?.trim() || '';

    setPendingToResolve(pending);
    setResolvePendingForm({
      product_id: pending.resolved_product_id ?? '',
      material_mode: pending.resolved_material_id ? 'existing' : 'create',
      material_id: pending.resolved_material_id ?? '',
      material_code: sourceMaterialRef,
      material_name: sourceMaterialRef,
      material_type: '',
      unit_label: pending.unit_label ?? '',
      is_active: true,
      description: pending.source_material_ref
        ? `Creado desde pendiente de consumo técnico: ${pending.source_material_ref}`
        : '',
    });
  }

  function closeResolvePendingDialog() {
    if (resolvePendingMutation.isPending) return;

    setPendingToResolve(null);
    setResolvePendingForm(initialResolvePendingForm);
  }

  const canResolvePending =
    Boolean(pendingToResolve) &&
    resolvePendingForm.product_id.trim().length > 0 &&
    pendingToResolve?.quantity !== null &&
    pendingToResolve?.quantity !== undefined &&
    Number(pendingToResolve.quantity) > 0 &&
    (resolvePendingForm.material_mode === 'existing'
      ? resolvePendingForm.material_id.trim().length > 0
      : resolvePendingForm.material_code.trim().length > 0);

  function confirmResolvePending() {
    if (!pendingToResolve || !canResolvePending) return;

    const payload: Parameters<typeof resolveIndustrialCostTechnicalConsumptionImportPending>[1] =
      resolvePendingForm.material_mode === 'existing'
        ? {
            product_id: resolvePendingForm.product_id,
            material_id: resolvePendingForm.material_id,
            create_material: null,
          }
        : {
            product_id: resolvePendingForm.product_id,
            material_id: null,
            create_material: buildCreateMaterialPayload(resolvePendingForm),
          };

    resolvePendingMutation.mutate({
      pendingId: pendingToResolve.id,
      payload,
    });
  }

  function confirmDeleteRule() {
    if (!ruleToDelete) return;
    deleteMutation.mutate(ruleToDelete.id);
  }

  function confirmDiscardPending() {
    if (!pendingToDiscard) return;
    discardPendingMutation.mutate(pendingToDiscard.id);
  }

  const handleSubmit = () => {
    if (!canSubmit) return;

    const payload: CreateTechnicalConsumptionRulePayload = {
      product_id: form.product_id,
      material_id: form.material_id,
      quantity: Number(form.quantity),
      consumption_basis: form.consumption_basis || null,
      unit_label: form.unit_label.trim() || null,
      input_type: (form.input_type.trim() as CreateTechnicalConsumptionRulePayload['input_type']) || null,
      valid_from: form.valid_from || null,
      valid_to: form.valid_to || null,
      is_active: form.is_active,
      notes: form.notes.trim() || null,
    };

    if (editingRule) {
      updateMutation.mutate({
        ruleId: editingRule.id,
        payload,
      });
      return;
    }

    createMutation.mutate(payload);
  };

  async function runImportPreview(
    file: File,
    mapping?: Record<string, string | null>,
    nextProductResolution?: Record<string, string | null>,
    nextMaterialResolution?: Record<string, string | null>,
    normalization?: {
      sheet_name?: string | null;
      header_row_index?: number | null;
    },
  ): Promise<TechnicalConsumptionImportPreviewResponse | null> {
    setPreviewingImport(true);
    setImportError(null);
    setImportResultMessage(null);

    const effectiveNormalization =
      normalization ??
      (normalizationResult
        ? {
            sheet_name: normalizationResult.selected_sheet,
            header_row_index: normalizationResult.header_row_index,
          }
        : undefined);

    try {
      const preview =
        await previewIndustrialCostTechnicalConsumptionRulesImport(
          file,
          mapping,
          nextProductResolution,
          nextMaterialResolution,
          effectiveNormalization,
        );

      setImportPreview(preview);
      setImportMapping(preview.auto_mapping);
      setProductResolution(nextProductResolution ?? productResolution);
      setMaterialResolution(nextMaterialResolution ?? materialResolution);
      setMaterialTypeMapping((current) => {
        const next: Record<string, string | null> = {};

        preview.detected_material_type_values.forEach((item) => {
          next[item.source_value] =
            current[item.source_value] ??
            item.resolved_material_type ??
            null;
        });

        return next;
      });
      setImportMappingDirty(false);
      setImportDialogOpen(true);

      return preview;
    } catch (err: any) {
      setImportError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo leer el archivo de consumos técnicos.',
      );

      return null;
    } finally {
      setPreviewingImport(false);
    }
  }

  // IND-007
  const canImport = useCanManageImports();

  function handleOpenImportPicker() {
    if (!canImport) return;
    importInputRef.current?.click();
  }

  function handleOpenNormalizationReview() {
    if (!normalizationResult) return;

    setNormalizationReviewSheetName(
      normalizationResult.selected_sheet ?? '',
    );
    setNormalizationReviewHeaderRowInput(
      String(normalizationResult.header_row_index + 1),
    );
    setNormalizationReviewOpen(true);
  }

  function handleCancelNormalizationReview() {
    if (reviewingNormalization) return;

    setNormalizationReviewOpen(false);

    if (normalizationResult) {
      setNormalizationReviewSheetName(
        normalizationResult.selected_sheet ?? '',
      );
      setNormalizationReviewHeaderRowInput(
        String(normalizationResult.header_row_index + 1),
      );
    }
  }

  async function handleApplyNormalizationReview() {
    if (!importFile) return;

    const parsedHeaderRow = Number(normalizationReviewHeaderRowInput);

    if (!Number.isInteger(parsedHeaderRow) || parsedHeaderRow < 1) {
      setImportError(
        'La fila de cabecera debe ser un número entero mayor o igual que 1.',
      );
      return;
    }

    try {
      setReviewingNormalization(true);
      setImportError(null);

      const reviewedNormalization = await reviewFileNormalization(
        importFile,
        'technical_consumption_rules',
        parsedHeaderRow - 1,
        normalizationReviewSheetName || null,
      );

      setNormalizationResult(reviewedNormalization);
      setNormalizationReviewSheetName(
        reviewedNormalization.selected_sheet ?? '',
      );
      setNormalizationReviewHeaderRowInput(
        String(reviewedNormalization.header_row_index + 1),
      );
      setNormalizationReviewOpen(false);

      await runImportPreview(
        importFile,
        undefined,
        undefined,
        undefined,
        {
          sheet_name: reviewedNormalization.selected_sheet,
          header_row_index: reviewedNormalization.header_row_index,
        },
      );
    } catch (err: any) {
      setImportError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo reaplicar la normalización manual.',
      );
    } finally {
      setReviewingNormalization(false);
    }
  }

  async function handleSelectImportFile(file: File | null) {
    if (!file) return;

    setImportFile(file);
    setImportDialogOpen(true);
    setImportStep(1);
    setImportPreview(null);
    setImportMapping({});
    setImportMappingDirty(false);
    setProductResolution({});
    setMaterialResolution({});
    setMaterialTypeMapping({});
    setQuantityDivisor('1');
    setNormalizationResult(null);
    setNormalizationReviewOpen(false);
    setImportError(null);
    setImportResultMessage(null);

    try {
      const normalization = await analyzeFileNormalization(
        file,
        'technical_consumption_rules',
      );

      setNormalizationResult(normalization);
      setNormalizationReviewSheetName(
        normalization.selected_sheet ?? '',
      );
      setNormalizationReviewHeaderRowInput(
        String(normalization.header_row_index + 1),
      );

      await runImportPreview(
        file,
        normalization.applied_column_mapping ?? undefined,
        undefined,
        undefined,
        {
          sheet_name: normalization.selected_sheet,
          header_row_index: normalization.header_row_index,
        },
      );
    } catch (err: any) {
      setImportError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo analizar el archivo antes de generar la previsualización.',
      );
    }
  }

  function handleCloseImportDialog() {
    if (confirmingImport) return;

    setImportDialogOpen(false);
    setImportStep(1);
    setImportFile(null);
    setImportPreview(null);
    setImportMapping({});
    setImportMappingDirty(false);
    setProductResolution({});
    setMaterialResolution({});
    setMaterialTypeMapping({});
    setQuantityDivisor('1');
    setNormalizationResult(null);
    setNormalizationReviewOpen(false);
    setNormalizationReviewSheetName('');
    setNormalizationReviewHeaderRowInput('1');
    setImportError(null);

    if (importInputRef.current) {
      importInputRef.current.value = '';
    }
  }


  function handleChangeImportMapping(field: string, column: string) {
    if (!importFile) return;

    const nextMapping = {
      ...importMapping,
      [field]: column || null,
    };

    setImportMapping(nextMapping);
    setImportMappingDirty(true);
    setImportError(null);

    if (field === 'product') {
      setProductResolution({});
    }

    if (field === 'material') {
      setMaterialResolution({});
    }

    if (field === 'input_type') {
      setMaterialTypeMapping({});
    }
  }

  function handleChangeProductResolution(
    sourceValue: string,
    productId: string,
  ) {
    if (!importFile) return;

    setProductResolution((current) => ({
      ...current,
      [sourceValue]: productId || null,
    }));
  }

  function handleChangeMaterialResolution(
    sourceValue: string,
    materialId: string,
  ) {
    if (!importFile) return;

    setMaterialResolution((current) => ({
      ...current,
      [sourceValue]: materialId || null,
    }));
  }

  function handleChangeMaterialTypeMapping(
    sourceValue: string,
    categoryCode: string,
  ) {
    setMaterialTypeMapping((current) => ({
      ...current,
      [sourceValue]: categoryCode || null,
    }));
  }

  async function handleContinueFromColumns() {
    if (!importFile) return;

    const hasRequiredMapping = Boolean(
      importMapping.product &&
        importMapping.material &&
        importMapping.quantity,
    );

    if (!hasRequiredMapping) {
      setImportError(
        'Debes mapear Producto, Material / input y Cantidad técnica.',
      );
      return;
    }

    if (
      !importMappingDirty &&
      importPreview &&
      importPreview.missing_required_fields.length === 0
    ) {
      setImportStep(2);
      return;
    }

    const refreshedPreview = await runImportPreview(
      importFile,
      importMapping,
      productResolution,
      materialResolution,
    );

    if (
      refreshedPreview &&
      refreshedPreview.missing_required_fields.length === 0
    ) {
      setImportStep(2);
    }
  }

  async function handleConfirmImport() {
    if (!importFile || !importPreview) return;

    if (
      replaceExistingRules &&
      !window.confirm(
        'Sustituir escandallo: para cada producto del fichero se desactivarán sus reglas activas que no estén en él (no se borran). Los productos que no vengan en el fichero no se tocan. ¿Continuar?',
      )
    ) {
      return;
    }

    setConfirmingImport(true);
    setImportError(null);
    setImportResultMessage(null);

    try {
      const result = await confirmIndustrialCostTechnicalConsumptionRulesImport(
        importFile,
        importMapping,
        productResolution,
        materialResolution,
        materialTypeMapping,
        Number(quantityDivisor),
        normalizationResult
          ? {
              sheet_name: normalizationResult.selected_sheet,
              header_row_index: normalizationResult.header_row_index,
            }
          : undefined,
        replaceExistingRules,
      );

      if (normalizationResult) {
        try {
          await commitFileNormalization({
            importer_key: 'technical_consumption_rules',
            sheet_name: normalizationResult.selected_sheet,
            header_row_index: normalizationResult.header_row_index,
            data_start_row_index:
              normalizationResult.header_row_index + 1,
            column_mapping: Object.fromEntries(
              Object.entries(importMapping).filter(
                ([, value]) => value !== null,
              ) as [string, string][],
            ),
            known_columns: Object.values(importMapping).filter(
              (value): value is string => value !== null,
            ),
          });
        } catch {
          // Guardar el perfil de normalización no bloquea la importación.
        }
      }

      const skipped =
        result.rows_skipped_duplicate_in_file +
        result.rows_skipped_unresolved_product +
        result.rows_skipped_unresolved_material +
        result.rows_skipped_invalid_quantity;

      setImportResultMessage(
        `Importación completada: ${result.rows_imported} reglas importadas (${result.rows_created} creadas, ${result.rows_updated} actualizadas). Materiales clasificados: ${result.materials_classified}. Unidades completadas: ${result.materials_unit_completed}. Conflictos de clasificación: ${result.material_classification_conflicts}. Pendientes de resolución: ${result.rows_pending_created}. Filas no importadas: ${Math.max(
          skipped - result.rows_pending_created,
          0,
        )}.${
          result.replace_existing
            ? ` Reglas antiguas desactivadas: ${result.rules_deactivated ?? 0}.`
            : ''
        }${
          (result.rows_skipped_test_article ?? 0) > 0
            ? ` Líneas informativas (9999) ignoradas: ${result.rows_skipped_test_article}.`
            : ''
        }${
          result.warnings.length > 0
            ? ` Avisos: ${result.warnings
                .map((warning) => String(warning.message ?? ''))
                .filter(Boolean)
                .join(' ')}`
            : ''
        }`,
      );

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-technical-consumption-rules'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-technical-consumption-import-pending'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-products'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-direct-materials'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-direct-cost-categories'],
        }),
      ]);

      handleCloseImportDialog();
    } catch (err: any) {
      setImportError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo confirmar la importación de consumos técnicos.',
      );
    } finally {
      setConfirmingImport(false);
    }
  }

  const canPrepareImport = Boolean(
    importFile &&
      importMapping.product &&
      importMapping.material &&
      importMapping.quantity,
  );

  const canContinueImport =
    Boolean(importPreview) &&
    !importMappingDirty &&
    (importPreview?.missing_required_fields.length ?? 1) === 0;

  const unresolvedProductRefs =
    importPreview?.detected_product_refs.filter((item) => !item.is_resolved) ?? [];

  const unresolvedMaterialRefs =
    importPreview?.detected_material_refs.filter((item) => !item.is_resolved) ?? [];

  const unresolvedMaterialTypeValues =
    importPreview?.detected_material_type_values.filter(
      (item) => !materialTypeMapping[item.source_value],
    ) ?? [];

  const materialTypeColumnMapped = Boolean(
    importMapping.input_type,
  );

  const canConfirmImport =
    canContinueImport &&
    (
      !materialTypeColumnMapped ||
      unresolvedMaterialTypeValues.length === 0
    );

  return (
    <Stack spacing={3}>
      <input
        ref={importInputRef}
        type="file"
        accept=".csv,.xlsx"
        hidden
        onChange={(event) => handleSelectImportFile(event.target.files?.[0] ?? null)}
      />
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Consumos técnicos
          </Typography>

          <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
            Reglas que conectan cada producto final con los materiales directos que consume
            técnicamente para fabricarse.
          </Typography>

          <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
            Aquí no se registran compras ni producción real. Se define la receta técnica que
            después permite derivar cantidades e importes de materiales por producto.
          </Typography>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(260px, 1fr)' },
            gap: 2.5,
            alignItems: 'stretch',
          }}
        >
          <Stack spacing={2.25}>
            <Box>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Cobertura técnica
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', mt: 0.5 }}>
                Estado de las relaciones producto-material disponibles para derivar consumo y coste.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {[
                { label: 'Reglas totales', value: rows.length },
                { label: 'Reglas activas', value: activeRulesCount },
                { label: 'Productos con reglas', value: productsWithRulesCount },
                { label: 'Productos sin reglas', value: productsWithoutRulesCount },
                { label: 'Materiales usados', value: materialsUsedCount },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(2,6,23,0.32)',
                    px: 1.75,
                    py: 1.35,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.35rem' }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Box
                sx={{
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.72)',
                  color: '#cbd5e1',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.82rem',
                  fontWeight: 800,
                }}
              >
                Sin base explícita · {rulesWithoutExplicitBasisCount}
              </Box>

              <Box
                sx={{
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.72)',
                  color: '#cbd5e1',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.82rem',
                  fontWeight: 800,
                }}
              >
                Base por unidad · {rows.filter((row) => row.consumption_basis === 'per_unit').length}
              </Box>

              <Box
                sx={{
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.72)',
                  color: '#cbd5e1',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.82rem',
                  fontWeight: 800,
                }}
              >
                Base por kg · {rows.filter((row) => row.consumption_basis === 'per_kg').length}
              </Box>
            </Box>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(34,211,238,0.18)',
              bgcolor: 'rgba(8,47,73,0.16)',
              p: 2,
              alignSelf: 'stretch',
            }}
          >
            <Stack spacing={1.5} sx={{ height: '100%' }}>
              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Acciones
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mt: 0.35 }}>
                  Alta manual o carga masiva de reglas técnicas.
                </Typography>
              </Box>

              <Stack spacing={1} sx={{ mt: 'auto' }}>
                {!canImport ? (
                  <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                    {IMPORT_PERMISSION_DENIED_TEXT}
                  </Typography>
                ) : null}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenImportPicker}
                  disabled={previewingImport || !canImport}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {previewingImport ? 'Leyendo archivo...' : 'Importar consumos'}
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={openCreateRuleDialog}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: '#38bdf8',
                    color: '#0f172a',
                    '&:hover': {
                      bgcolor: '#38bdf8',
                      filter: 'brightness(1.06)',
                    },
                  }}
                >
                  Nueva regla
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Paper>

      {importResultMessage ? (
        <Alert severity="success" onClose={() => setImportResultMessage(null)}>
          {importResultMessage}
        </Alert>
      ) : null}

      {pendingRows.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(251,191,36,0.24)',
            bgcolor: 'rgba(120,53,15,0.12)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2, md: 2.25 },
          }}
        >
          <Stack spacing={1.75}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={1}
            >
              <Box>
                <Typography sx={{ color: '#fef3c7', fontWeight: 900 }}>
                  Pendientes de resolución · {pendingRows.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fde68a', mt: 0.35 }}>
                  Líneas importadas que no pudieron convertirse todavía en reglas válidas.
                </Typography>
              </Box>

              <Chip
                size="small"
                label="Afectan a cobertura de coste"
                sx={{
                  alignSelf: { xs: 'flex-start', md: 'center' },
                  borderColor: 'rgba(251,191,36,0.35)',
                  color: '#fef3c7',
                  bgcolor: 'rgba(251,191,36,0.08)',
                  fontWeight: 800,
                }}
                variant="outlined"
              />
            </Stack>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#fde68a', fontWeight: 800 }}>Fila</TableCell>
                    <TableCell sx={{ color: '#fde68a', fontWeight: 800 }}>Motivo</TableCell>
                    <TableCell sx={{ color: '#fde68a', fontWeight: 800 }}>Producto origen</TableCell>
                    <TableCell sx={{ color: '#fde68a', fontWeight: 800 }}>Material origen</TableCell>
                    <TableCell sx={{ color: '#fde68a', fontWeight: 800 }} align="right">
                      Cantidad
                    </TableCell>
                    <TableCell sx={{ color: '#fde68a', fontWeight: 800 }}>Acción requerida</TableCell>
                    <TableCell sx={{ color: '#fde68a', fontWeight: 800 }} align="right">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pendingRows.slice(0, 5).map((pending) => (
                    <TableRow key={pending.id} hover>
                      <TableCell sx={{ color: '#fef3c7' }}>
                        {pending.source_row_number ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#fef3c7', fontWeight: 800 }}>
                        {formatPendingReason(pending.pending_reason)}
                      </TableCell>
                      <TableCell sx={{ color: '#fef3c7' }}>
                        {pending.source_product_ref ||
                          pending.resolved_product_name ||
                          pending.resolved_product_code ||
                          '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#fef3c7' }}>
                        {pending.source_material_ref ||
                          pending.resolved_material_name ||
                          pending.resolved_material_code ||
                          '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fef3c7' }}>
                        {pending.quantity ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#fef3c7' }}>
                        {pending.action_required || 'Revisar línea pendiente.'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleResolvePending(pending)}
                          disabled={resolvePendingMutation.isPending}
                          sx={{
                            mr: 1,
                            textTransform: 'none',
                            fontWeight: 800,
                            borderColor: 'rgba(56,189,248,0.42)',
                            color: '#e0f2fe',
                            '&:hover': {
                              borderColor: 'rgba(56,189,248,0.7)',
                              bgcolor: 'rgba(56,189,248,0.10)',
                            },
                          }}
                        >
                          Resolver
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDiscardPending(pending)}
                          disabled={discardPendingMutation.isPending}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 800,
                            borderColor: 'rgba(251,191,36,0.36)',
                            color: '#fef3c7',
                            '&:hover': {
                              borderColor: 'rgba(251,191,36,0.6)',
                              bgcolor: 'rgba(251,191,36,0.08)',
                            },
                          }}
                        >
                          Descartar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {pendingRows.length > 5 ? (
              <Typography variant="caption" sx={{ color: '#fde68a' }}>
                Mostrando 5 de {pendingRows.length} pendientes. Usa Resolver para convertir una línea pendiente en regla técnica real.
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Reglas de consumo técnico
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Una fila por relación producto-input. La tabla define qué consume cada producto final.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(240px, 1.2fr) minmax(190px, 1fr) minmax(190px, 1fr) minmax(140px, 0.7fr)',
                  },
                  gap: 1.25,
                  minWidth: { xs: '100%', md: 780 },
                }}
              >
                <TextField
                  label="Buscar"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Producto, material, unidad, notas..."
                  fullWidth
                  size="small"
                />

                <Autocomplete
                  options={products}
                  value={
                    productFilter === 'all'
                      ? null
                      : products.find((product) => product.id === productFilter) ?? null
                  }
                  onChange={(_, value) =>
                    setProductFilter(value?.id ?? 'all')
                  }
                  getOptionLabel={(product) =>
                    product.code
                      ? `${product.code} · ${product.name}`
                      : product.name
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Producto"
                      size="small"
                      fullWidth
                    />
                  )}
                />

                <Autocomplete
                  options={materials}
                  value={
                    materialFilter === 'all'
                      ? null
                      : materials.find((material) => material.id === materialFilter) ?? null
                  }
                  onChange={(_, value) =>
                    setMaterialFilter(value?.id ?? 'all')
                  }
                  getOptionLabel={(material) =>
                    material.code
                      ? `${material.code} · ${material.name}`
                      : material.name
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Input"
                      size="small"
                      fullWidth
                    />
                  )}
                />

                <TextField
                  select
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
                  }
                  fullWidth
                  size="small"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Activas</MenuItem>
                  <MenuItem value="inactive">Inactivas</MenuItem>
                </TextField>
              </Box>
            </Stack>

            <Alert severity="info">
              Si una regla no informa base de consumo, el sistema podrá asumir después{' '}
              <strong>por unidad final</strong> como comportamiento por defecto.
            </Alert>
          </Stack>
        </Box>

        {rulesQuery.isLoading || productsQuery.isLoading || directMaterialsQuery.isLoading || directCostCategoriesQuery.isLoading ? (
          <Box sx={{ p: 3 }}>
            <IndustrialCostSectionPlaceholder
              title="Cargando consumos técnicos"
              description="Estamos recuperando reglas, productos y materiales directos."
            />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <IndustrialCostSectionPlaceholder
              title="Sin consumos técnicos todavía"
              description="Aquí aparecerán las reglas que definen qué inputs consume cada producto final."
            />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <IndustrialCostSectionPlaceholder
              title="Sin resultados para el filtro actual"
              description="Ajusta los filtros para volver a mostrar reglas de consumo técnico."
            />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Producto final</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Input</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Base</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Unidad</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Tipo</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Válida desde</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Válida hasta</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Estado</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Notas</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.slice(0, 100).map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                      {row.product_name || row.product_code || row.product_id}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>
                      {row.material_name || row.material_code || row.material_id}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                      {row.quantity}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>
                      {formatBasis(row.consumption_basis)}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.unit_label || '—'}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.input_type || '—'}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.valid_from || '—'}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.valid_to || '—'}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>
                      {row.is_active ? 'Activa' : 'Inactiva'}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.notes || '—'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          onClick={() => openEditRuleDialog(row)}
                          sx={{ color: '#38bdf8' }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRule(row)}
                          disabled={deleteMutation.isPending}
                          sx={{ color: '#f87171' }}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredRows.length > 100 ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  py: 1.5,
                  color: '#94a3b8',
                }}
              >
                Mostrando las primeras 100 de {filteredRows.length} reglas.
                Utiliza los filtros o la búsqueda para localizar una regla concreta.
              </Typography>
            ) : null}
          </Box>
        )}
      </Paper>

      <Dialog
        open={createOpen}
        onClose={closeRuleDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingRule ? 'Editar regla de consumo técnico' : 'Nueva regla de consumo técnico'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" variant="outlined">
              En esta fase basta con informar <strong>producto final</strong>, <strong>input</strong>{' '}
              y <strong>cantidad</strong>. La base de consumo es opcional y, si falta, podrá
              asumirse después como <strong>por unidad final</strong>.
            </Alert>

            <TextField
              select
              label="Producto final"
              value={form.product_id}
              onChange={(e) => setForm((prev) => ({ ...prev, product_id: e.target.value }))}
              fullWidth
              size="small"
            >
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Input"
              value={form.material_id}
              onChange={(e) => setForm((prev) => ({ ...prev, material_id: e.target.value }))}
              fullWidth
              size="small"
            >
              {materials.map((material) => (
                <MenuItem key={material.id} value={material.id}>
                  {material.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Cantidad"
              type="number"
              value={form.quantity}
              onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
              fullWidth
              size="small"
            />

            <TextField
              select
              label="Base de consumo"
              value={form.consumption_basis}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  consumption_basis: e.target.value as '' | TechnicalConsumptionBasis,
                }))
              }
              fullWidth
              size="small"
            >
              <MenuItem value="">Por unidad (default)</MenuItem>
              <MenuItem value="per_unit">Por unidad</MenuItem>
              <MenuItem value="per_kg">Por kg</MenuItem>
            </TextField>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              <TextField
                label="Unidad"
                value={form.unit_label}
                onChange={(e) => setForm((prev) => ({ ...prev, unit_label: e.target.value }))}
                fullWidth
                size="small"
              />

              <TextField
                select
                label="Tipo de input"
                value={form.input_type}
                onChange={(e) => setForm((prev) => ({ ...prev, input_type: e.target.value }))}
                fullWidth
                size="small"
              >
                <MenuItem value="">Sin informar</MenuItem>
                <MenuItem value="raw_material">Materia prima</MenuItem>
                <MenuItem value="packaging">Packaging</MenuItem>
                <MenuItem value="ingredient">Ingrediente</MenuItem>
                <MenuItem value="consumable">Consumible</MenuItem>
                <MenuItem value="other">Otro</MenuItem>
              </TextField>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              <TextField
                label="Válida desde"
                type="date"
                value={form.valid_from}
                onChange={(e) => setForm((prev) => ({ ...prev, valid_from: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Válida hasta"
                type="date"
                value={form.valid_to}
                onChange={(e) => setForm((prev) => ({ ...prev, valid_to: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="Notas"
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
              }
              label="Regla activa"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeRuleDialog}
            disabled={createMutation.isPending || updateMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || createMutation.isPending || updateMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Guardando...'
              : editingRule
                ? 'Guardar cambios'
                : 'Guardar regla'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(ruleToDelete)}
        onClose={() => {
          if (!deleteMutation.isPending) {
            setRuleToDelete(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Eliminar regla técnica</DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography sx={{ color: '#cbd5e1' }}>
              Esta regla técnica se eliminará del modelo y puede cambiar la derivación de costes
              del producto.
            </Typography>

            {ruleToDelete ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.58)',
                  p: 2,
                }}
              >
                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  {ruleToDelete.product_name || ruleToDelete.product_code || ruleToDelete.product_id}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Input:{' '}
                  {ruleToDelete.material_name || ruleToDelete.material_code || ruleToDelete.material_id}
                  {' · '}
                  Cantidad: {ruleToDelete.quantity}
                </Typography>
              </Paper>
            ) : null}

            <Alert severity="warning">
              Esta acción no se debe usar para corregir una línea si simplemente quieres cambiar
              cantidad, material o producto. En ese caso, usa Editar.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setRuleToDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={confirmDeleteRule}
            disabled={deleteMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#ef4444',
              color: '#fff',
              '&:hover': {
                bgcolor: '#dc2626',
              },
            }}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar regla'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingToDiscard)}
        onClose={() => {
          if (!discardPendingMutation.isPending) {
            setPendingToDiscard(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Descartar pendiente</DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography sx={{ color: '#cbd5e1' }}>
              Esta línea dejará de aparecer como pendiente. No se creará ninguna regla técnica
              a partir de ella.
            </Typography>

            {pendingToDiscard ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(251,191,36,0.20)',
                  bgcolor: 'rgba(120,53,15,0.14)',
                  p: 2,
                }}
              >
                <Typography sx={{ color: '#fef3c7', fontWeight: 900 }}>
                  {formatPendingReason(pendingToDiscard.pending_reason)}
                </Typography>

                <Typography variant="body2" sx={{ color: '#fde68a', mt: 0.5 }}>
                  Producto origen:{' '}
                  {pendingToDiscard.source_product_ref ||
                    pendingToDiscard.resolved_product_name ||
                    pendingToDiscard.resolved_product_code ||
                    '—'}
                </Typography>

                <Typography variant="body2" sx={{ color: '#fde68a' }}>
                  Material origen:{' '}
                  {pendingToDiscard.source_material_ref ||
                    pendingToDiscard.resolved_material_name ||
                    pendingToDiscard.resolved_material_code ||
                    '—'}
                </Typography>

                <Typography variant="body2" sx={{ color: '#fde68a' }}>
                  Cantidad: {pendingToDiscard.quantity ?? '—'}
                </Typography>
              </Paper>
            ) : null}

            <Alert severity="warning">
              Úsalo solo si esta línea no debe formar parte del modelo de coste o si vas a repetir
              la importación con datos corregidos.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setPendingToDiscard(null)}
            disabled={discardPendingMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={confirmDiscardPending}
            disabled={discardPendingMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#f59e0b',
              color: '#111827',
              '&:hover': {
                bgcolor: '#d97706',
              },
            }}
          >
            {discardPendingMutation.isPending ? 'Descartando...' : 'Descartar pendiente'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingToResolve)}
        onClose={closeResolvePendingDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Resolver pendiente</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" variant="outlined">
              Esta acción convierte la línea pendiente en una regla técnica real. Puedes vincularla
              a un material existente o crear un nuevo subproducto/material usando el mismo criterio
              que en el catálogo maestro.
            </Alert>

            {pendingToResolve ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.20)',
                  bgcolor: 'rgba(15,23,42,0.55)',
                  p: 2,
                }}
              >
                <Stack spacing={0.75}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    {formatPendingReason(pendingToResolve.pending_reason)}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Producto origen:{' '}
                    {pendingToResolve.source_product_ref ||
                      pendingToResolve.resolved_product_name ||
                      pendingToResolve.resolved_product_code ||
                      '—'}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Material origen:{' '}
                    {pendingToResolve.source_material_ref ||
                      pendingToResolve.resolved_material_name ||
                      pendingToResolve.resolved_material_code ||
                      '—'}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Cantidad técnica: {pendingToResolve.quantity ?? '—'} ·{' '}
                    {formatBasis(pendingToResolve.consumption_basis)}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            <Autocomplete
              options={products}
              value={products.find((product) => product.id === resolvePendingForm.product_id) ?? null}
              getOptionLabel={(option) => `${option.code ? `${option.code} · ` : ''}${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) =>
                setResolvePendingForm((prev) => ({
                  ...prev,
                  product_id: value?.id ?? '',
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Producto final"
                  size="small"
                  required
                />
              )}
            />

            <TextField
              select
              label="Cómo resolver el material"
              value={resolvePendingForm.material_mode}
              onChange={(e) =>
                setResolvePendingForm((prev) => ({
                  ...prev,
                  material_mode: e.target.value as ResolvePendingMaterialMode,
                }))
              }
              fullWidth
              size="small"
            >
              <MenuItem value="existing">Usar material/subproducto existente</MenuItem>
              <MenuItem value="create">Crear nuevo subproducto/material</MenuItem>
            </TextField>

            {resolvePendingForm.material_mode === 'existing' ? (
              <Autocomplete
                options={materials}
                value={materials.find((material) => material.id === resolvePendingForm.material_id) ?? null}
                getOptionLabel={(option) =>
                  `${option.code ? `${option.code} · ` : ''}${option.name}${
                    option.material_type ? ` · ${getCategoryLabel(categories, option.material_type)}` : ''
                  }`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setResolvePendingForm((prev) => ({
                    ...prev,
                    material_id: value?.id ?? '',
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Material/subproducto existente"
                    size="small"
                    required
                  />
                )}
              />
            ) : (
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    label="Código"
                    value={resolvePendingForm.material_code}
                    onChange={(e) =>
                      setResolvePendingForm((prev) => ({
                        ...prev,
                        material_code: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    required
                  />

                  <TextField
                    label="Nombre"
                    value={resolvePendingForm.material_name}
                    onChange={(e) =>
                      setResolvePendingForm((prev) => ({
                        ...prev,
                        material_name: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    helperText="Si se deja vacío, se usará el código."
                  />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    select
                    label="Clasificación"
                    value={resolvePendingForm.material_type}
                    onChange={(e) =>
                      setResolvePendingForm((prev) => ({
                        ...prev,
                        material_type: e.target.value as DirectMaterialType | '',
                      }))
                    }
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Sin clasificación</MenuItem>
                    {categories
                      .filter((category) => category.is_active)
                      .map((category) => (
                        <MenuItem key={category.id} value={category.code}>
                          {category.label}
                        </MenuItem>
                      ))}
                  </TextField>

                  <TextField
                    label="Unidad"
                    value={resolvePendingForm.unit_label}
                    onChange={(e) =>
                      setResolvePendingForm((prev) => ({
                        ...prev,
                        unit_label: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Box>

                <TextField
                  label="Estado"
                  select
                  value={resolvePendingForm.is_active ? 'active' : 'inactive'}
                  onChange={(e) =>
                    setResolvePendingForm((prev) => ({
                      ...prev,
                      is_active: e.target.value === 'active',
                    }))
                  }
                  fullWidth
                  size="small"
                >
                  <MenuItem value="active">Activo</MenuItem>
                  <MenuItem value="inactive">Inactivo</MenuItem>
                </TextField>

                <TextField
                  label="Descripción"
                  value={resolvePendingForm.description}
                  onChange={(e) =>
                    setResolvePendingForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                />
              </Stack>
            )}

            <Alert severity="warning" variant="outlined">
              Al resolver, la pendiente desaparecerá del bloque de pendientes y se creará o
              actualizará la regla técnica correspondiente.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeResolvePendingDialog}
            disabled={resolvePendingMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={confirmResolvePending}
            disabled={!canResolvePending || resolvePendingMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {resolvePendingMutation.isPending ? 'Resolviendo...' : 'Crear regla y resolver'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Importar consumos técnicos</DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {importError ? (
              <Alert severity="error">{importError}</Alert>
            ) : null}

            {normalizationResult ? (
              <Stack spacing={1.5}>
                <Alert
                  severity={
                    normalizationResult.warnings.length > 0
                      ? 'warning'
                      : 'info'
                  }
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={handleOpenNormalizationReview}
                      disabled={reviewingNormalization}
                    >
                      Revisar normalización
                    </Button>
                  }
                >
                  <Stack spacing={0.5}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {normalizationResult.source === 'profile'
                        ? 'Configuración recordada aplicada'
                        : normalizationResult.source === 'manual_review'
                          ? 'Configuración revisada manualmente'
                          : 'Detección automática aplicada'}
                    </Typography>

                    <Typography variant="body2">
                      {normalizationResult.selected_sheet
                        ? `Hoja: ${normalizationResult.selected_sheet} · `
                        : ''}
                      Cabecera: fila{' '}
                      {normalizationResult.header_row_index + 1}
                    </Typography>
                  </Stack>
                </Alert>

                {normalizationReviewOpen ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid rgba(148,163,184,0.18)',
                      bgcolor: 'rgba(15,23,42,0.58)',
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography
                        sx={{ color: '#f8fafc', fontWeight: 800 }}
                      >
                        Revisar normalización
                      </Typography>

                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                      >
                        {normalizationResult.sheets.length > 0 ? (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Hoja"
                            value={normalizationReviewSheetName}
                            onChange={(event) =>
                              setNormalizationReviewSheetName(
                                event.target.value,
                              )
                            }
                          >
                            {normalizationResult.sheets.map((sheet) => (
                              <MenuItem
                                key={sheet.name}
                                value={sheet.name}
                              >
                                {sheet.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : null}

                        <TextField
                          fullWidth
                          size="small"
                          label="Fila de cabecera"
                          type="number"
                          value={normalizationReviewHeaderRowInput}
                          onChange={(event) =>
                            setNormalizationReviewHeaderRowInput(
                              event.target.value,
                            )
                          }
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1.5}
                        justifyContent="flex-end"
                      >
                        <Button
                          variant="outlined"
                          onClick={handleCancelNormalizationReview}
                          disabled={reviewingNormalization}
                        >
                          Cancelar revisión
                        </Button>

                        <Button
                          variant="contained"
                          onClick={() =>
                            void handleApplyNormalizationReview()
                          }
                          disabled={reviewingNormalization}
                        >
                          {reviewingNormalization
                            ? 'Aplicando...'
                            : 'Aplicar revisión'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ) : null}
              </Stack>
            ) : null}

            {importPreview?.row_warnings?.map((warning, index) => (
              <Alert severity="warning" key={`${warning.type ?? 'warning'}-${index}`}>
                {String(
                  warning.message ||
                    'Hay advertencias en la previsualización de la importación.',
                )}
              </Alert>
            ))}

            {importPreview ? (() => {
              const preview = importPreview;

              return (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        md: 'repeat(4, minmax(0, 1fr))',
                      },
                      gap: 1.25,
                    }}
                  >
                    {[
                      { label: 'Filas', value: preview.coverage_summary.total_rows },
                      { label: 'Importables', value: preview.coverage_summary.rows_importable },
                      { label: 'No importables', value: preview.coverage_summary.rows_not_importable },
                      { label: 'Productos sin resolver', value: preview.coverage_summary.unresolved_product_refs_count },
                      { label: 'Materiales sin resolver', value: preview.coverage_summary.unresolved_material_refs_count },
                      { label: 'Cantidad inválida', value: preview.coverage_summary.rows_blocked_invalid_quantity },
                      { label: 'Duplicados internos', value: preview.coverage_summary.rows_skipped_duplicate_in_file },
                      { label: 'Materiales detectados', value: preview.coverage_summary.detected_material_refs_count },
                    ].map((item) => (
                      <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid rgba(148,163,184,0.16)',
                          bgcolor: 'rgba(15,23,42,0.58)',
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.25rem' }}>
                          {item.value}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant={importStep === 1 ? 'contained' : 'outlined'}
                      onClick={() => setImportStep(1)}
                    >
                      1 · Columnas
                    </Button>
                    <Button
                      size="small"
                      variant={importStep === 2 ? 'contained' : 'outlined'}
                      onClick={() => canContinueImport && setImportStep(2)}
                      disabled={!canContinueImport}
                    >
                      2 · Productos
                    </Button>
                    <Button
                      size="small"
                      variant={importStep === 3 ? 'contained' : 'outlined'}
                      onClick={() => canContinueImport && setImportStep(3)}
                      disabled={!canContinueImport}
                    >
                      3 · Materiales
                    </Button>
                  </Stack>

                  {importStep === 1 ? (
                    <Stack spacing={2}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Mapeo de columnas
                      </Typography>

                      <TextField
                        label="Cantidad del archivo expresada por cada"
                        type="number"
                        value={quantityDivisor}
                        onChange={(event) =>
                          setQuantityDivisor(event.target.value)
                        }
                        helperText="Ej.: introduce 100 si el consumo del archivo está expresado por cada 100 unidades o metros producidos."
                        size="small"
                        sx={{ maxWidth: 420 }}
                      />

                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={replaceExistingRules}
                              onChange={(event) =>
                                setReplaceExistingRules(event.target.checked)
                              }
                            />
                          }
                          label="Sustituir escandallo completo"
                        />
                        <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
                          El fichero es el maestro de cada producto que trae:
                          sus reglas activas que no estén en el fichero se
                          desactivan (no se borran); los productos que no
                          vienen no se tocan. Hoy hay{' '}
                          {preview.existing_active_rules_count ?? '—'} reglas
                          activas y el fichero trae{' '}
                          {preview.coverage_summary.total_rows} filas.
                          No asignes a mano las cajas genéricas (p. ej. CAJA
                          PEQUEÑA): se crean solas.
                        </Typography>
                      </Box>

                      {preview.missing_required_fields.length > 0 ? (
                        <Alert severity="error">
                          Falta mapear campos obligatorios:{' '}
                          {preview.missing_required_fields.join(', ')}
                        </Alert>
                      ) : null}

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Campo PROA</TableCell>
                            <TableCell>Columna del archivo</TableCell>
                            <TableCell>Obligatorio</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {technicalConsumptionImportFields.map((field) => (
                            <TableRow key={field.key}>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700 }}>
                                  {field.label}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  value={importMapping[field.key] ?? ''}
                                  onChange={(event) =>
                                    handleChangeImportMapping(field.key, event.target.value)
                                  }
                                >
                                  <MenuItem value="">No mapear</MenuItem>
                                  {preview.columns.map((column) => (
                                    <MenuItem key={column} value={column}>
                                      {column}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>

                              <TableCell>
                                {field.required ? 'Sí' : 'No'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      <Box>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 800, mb: 1 }}>
                          Vista previa
                        </Typography>

                        <Box sx={{ overflowX: 'auto' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Producto</TableCell>
                                <TableCell>Línea</TableCell>
                                <TableCell>Material</TableCell>
                                <TableCell>Cantidad</TableCell>
                                <TableCell>Base</TableCell>
                                <TableCell>Unidad</TableCell>
                                <TableCell>Tipo</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {preview.preview_rows.map((row, index) => (
                                <TableRow key={index}>
                                  <TableCell>{String(row.product ?? '—')}</TableCell>
                                  <TableCell>{String(row.production_line ?? 'General')}</TableCell>
                                  <TableCell>{String(row.material ?? '—')}</TableCell>
                                  <TableCell>{String(row.quantity ?? '—')}</TableCell>
                                  <TableCell>{String(row.consumption_basis ?? '—')}</TableCell>
                                  <TableCell>{String(row.unit_label ?? '—')}</TableCell>
                                  <TableCell>{String(row.input_type ?? '—')}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    </Stack>
                  ) : null}

                  {importStep === 2 ? (
                    <Stack spacing={2}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Resolución de productos
                      </Typography>

                      {unresolvedProductRefs.length === 0 ? (
                        <Alert severity="success">
                          Todos los productos detectados están resueltos.
                        </Alert>
                      ) : (
                        <Alert severity="info">
                          Hay productos que todavía no existen en PROA.
                          Si no los homologas manualmente, se crearán automáticamente
                          utilizando la referencia del archivo.
                        </Alert>
                      )}

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Valor en Excel</TableCell>
                            <TableCell>Filas</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Producto PROA</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {preview.detected_product_refs
                            .slice(0, 50)
                            .map((item) => (
                              <TableRow key={item.normalized_value}>
                                <TableCell>{item.source_value}</TableCell>
                                <TableCell>{item.row_count}</TableCell>
                                <TableCell>
                                  {item.is_resolved
                                    ? `Resuelto${
                                        item.resolution_source
                                          ? ` · ${item.resolution_source}`
                                          : ''
                                      }`
                                    : 'Se creará automáticamente'}
                                </TableCell>

                                <TableCell sx={{ minWidth: 320 }}>
                                  <Autocomplete
                                    options={products}
                                    value={
                                      products.find(
                                        (product) =>
                                          product.id ===
                                          (
                                            productResolution[item.source_value] ??
                                            item.resolved_product_id ??
                                            ''
                                          ),
                                      ) ?? null
                                    }
                                    onChange={(_, value) =>
                                      handleChangeProductResolution(
                                        item.source_value,
                                        value?.id ?? '',
                                      )
                                    }
                                    getOptionLabel={(product) =>
                                      product.code
                                        ? `${product.code} · ${product.name}`
                                        : product.name
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Crear automáticamente"
                                      />
                                    )}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>

                      {preview.detected_product_refs.length > 50 ? (
                        <Typography
                          variant="caption"
                          sx={{ color: '#94a3b8' }}
                        >
                          Mostrando 50 de{' '}
                          {preview.detected_product_refs.length} referencias.
                          Las referencias no homologadas manualmente se crearán
                          automáticamente al confirmar.
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}

                  {importStep === 3 ? (
                    <Stack spacing={2}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Resolución de materiales
                      </Typography>

                      {unresolvedMaterialRefs.length === 0 ? (
                        <Alert severity="success">
                          Todos los materiales detectados están resueltos.
                        </Alert>
                      ) : (
                        <Alert severity="info">
                          Hay materiales que todavía no existen en PROA.
                          Si no los homologas manualmente, se crearán automáticamente
                          utilizando la referencia del archivo.
                        </Alert>
                      )}

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Valor en Excel</TableCell>
                            <TableCell>Filas</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Material PROA</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {preview.detected_material_refs
                            .slice(0, 50)
                            .map((item) => (
                              <TableRow key={item.normalized_value}>
                                <TableCell>{item.source_value}</TableCell>
                                <TableCell>{item.row_count}</TableCell>
                                <TableCell>
                                  {item.is_resolved
                                    ? `Resuelto${
                                        item.resolution_source
                                          ? ` · ${item.resolution_source}`
                                          : ''
                                      }`
                                    : 'Se creará automáticamente'}
                                </TableCell>

                                <TableCell sx={{ minWidth: 320 }}>
                                  <Autocomplete
                                    options={materials}
                                    value={
                                      materials.find(
                                        (material) =>
                                          material.id ===
                                          (
                                            materialResolution[item.source_value] ??
                                            item.resolved_material_id ??
                                            ''
                                          ),
                                      ) ?? null
                                    }
                                    onChange={(_, value) =>
                                      handleChangeMaterialResolution(
                                        item.source_value,
                                        value?.id ?? '',
                                      )
                                    }
                                    getOptionLabel={(material) =>
                                      material.code
                                        ? `${material.code} · ${material.name}`
                                        : material.name
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Crear automáticamente"
                                      />
                                    )}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>

                      {preview.detected_material_refs.length > 50 ? (
                        <Typography
                          variant="caption"
                          sx={{ color: '#94a3b8' }}
                        >
                          Mostrando 50 de{' '}
                          {preview.detected_material_refs.length} referencias.
                          Las referencias no homologadas manualmente se crearán
                          automáticamente al confirmar.
                        </Typography>
                      ) : null}

                      <Box
                        sx={{
                          borderTop: '1px solid rgba(148,163,184,0.18)',
                          pt: 2,
                          mt: 1,
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 800,
                              }}
                            >
                              Clasificación canónica de materiales
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#94a3b8',
                                mt: 0.5,
                              }}
                            >
                              El valor de Tipo de input del Excel se utiliza
                              únicamente para asignar la clasificación del
                              material maestro. Esa clasificación será la
                              fuente de verdad para materia prima,
                              embalajes y el cálculo de kg.
                            </Typography>
                          </Box>

                          {!materialTypeColumnMapped ? (
                            <Alert severity="warning">
                              No has mapeado Tipo de input. La importación
                              puede continuar, pero no podrá recuperar la
                              clasificación de los materiales desde este
                              archivo.
                            </Alert>
                          ) : null}

                          {materialTypeColumnMapped &&
                          preview.detected_material_type_values.length === 0 ? (
                            <Alert severity="warning">
                              La columna Tipo de input no contiene valores.
                            </Alert>
                          ) : null}

                          {materialTypeColumnMapped &&
                          unresolvedMaterialTypeValues.length > 0 ? (
                            <Alert severity="warning">
                              Debes relacionar todos los valores de Tipo de
                              input con una clasificación PROA antes de
                              confirmar.
                            </Alert>
                          ) : null}

                          {materialTypeColumnMapped &&
                          preview.detected_material_type_values.length > 0 ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Valor en Excel</TableCell>
                                  <TableCell>Filas</TableCell>
                                  <TableCell>
                                    Clasificación PROA
                                  </TableCell>
                                  <TableCell>
                                    Cuenta en kg MP
                                  </TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {preview.detected_material_type_values.map(
                                  (item) => {
                                    const selectedCode =
                                      materialTypeMapping[
                                        item.source_value
                                      ] ?? '';

                                    const selectedCategory =
                                      categories.find(
                                        (category) =>
                                          category.code ===
                                          selectedCode,
                                      );

                                    return (
                                      <TableRow
                                        key={item.normalized_value}
                                      >
                                        <TableCell>
                                          {item.source_value}
                                        </TableCell>
                                        <TableCell>
                                          {item.row_count}
                                        </TableCell>
                                        <TableCell
                                          sx={{ minWidth: 280 }}
                                        >
                                          <TextField
                                            select
                                            size="small"
                                            fullWidth
                                            value={selectedCode}
                                            onChange={(event) =>
                                              handleChangeMaterialTypeMapping(
                                                item.source_value,
                                                event.target.value,
                                              )
                                            }
                                          >
                                            <MenuItem value="">
                                              Sin resolver
                                            </MenuItem>
                                            {categories
                                              .filter(
                                                (category) =>
                                                  category.is_active,
                                              )
                                              .map((category) => (
                                                <MenuItem
                                                  key={category.id}
                                                  value={category.code}
                                                >
                                                  {category.label}
                                                </MenuItem>
                                              ))}
                                          </TextField>
                                        </TableCell>
                                        <TableCell>
                                          {selectedCategory
                                            ? selectedCategory.include_in_material_mass
                                              ? 'Sí'
                                              : 'No'
                                            : '—'}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  },
                                )}
                              </TableBody>
                            </Table>
                          ) : null}

                          {materialTypeColumnMapped &&
                          categories.filter(
                            (category) => category.is_active,
                          ).length === 0 ? (
                            <Alert severity="error">
                              No existen clasificaciones activas. Créelas
                              primero en Materiales antes de confirmar el
                              escandallo.
                            </Alert>
                          ) : null}
                        </Stack>
                      </Box>
                    </Stack>
                  ) : null}
                </>
              );
            })() : (
              <Alert severity="info">
                Selecciona un archivo CSV o XLSX para preparar la importación.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={confirmingImport}>
            Cancelar
          </Button>

          {importStep === 1 ? (
            <Button
              variant="contained"
              onClick={() => void handleContinueFromColumns()}
              disabled={!canPrepareImport || previewingImport}
            >
              {previewingImport ? 'Procesando...' : 'Continuar'}
            </Button>
          ) : importStep === 2 ? (
            <Button
              variant="contained"
              onClick={() => setImportStep(3)}
              disabled={!canContinueImport || previewingImport}
            >
              Continuar
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => void handleConfirmImport()}
              disabled={!canConfirmImport || confirmingImport}
            >
              {confirmingImport ? 'Importando...' : 'Confirmar importación'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}